import { StudentTipsApi } from "@features/shared/api/course-data.api";
import { IAiTipCategory, IAiTipItem } from "@features/shared/entities/tips";
import {
    ModelContract,
    ModuleDescription
} from "@features/shared/module-description";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { contentLocaleChanged } from "@features/shared/locale";

const setCategory = createEvent<number>();

const $categories = createStore<IAiTipCategory[]>([]);
const $selectedCategory = createStore<number | null>(null);
const $tips = createStore<IAiTipItem[]>([]);
const $featuredTips = createStore<IAiTipItem[]>([]);

const getCategoriesFx = createEffect(async () => {
    const response = await StudentTipsApi.getTipsCategories();

    return response;
});

const getFeaturedFx = createEffect(async () => {
    const response = await StudentTipsApi.getFeaturedTips();

    return response;
});

const getTipsFx = createEffect(async (categoryId: number) => {
    const response = await StudentTipsApi.getTipsByCategoryId(categoryId);

    return response;
});

sample({
    clock: getCategoriesFx.doneData,
    fn: (res)=>([{id: -100, caption: "Popular"}, ...res]),
    target: $categories
});

// Only auto-select on the first load. Categories are refetched on a language
// switch too, and without this guard that refetch would throw the user back to
// the first chip.
sample({
    clock: $categories,
    source: $selectedCategory,
    filter: (selected) => selected === null,
    fn: (_, categories) => categories[0]?.id ?? null,
    target: [$selectedCategory, getTipsFx]
});

sample({
    clock: setCategory,
    target: [$selectedCategory, getTipsFx]
});

sample({
    clock: getTipsFx.doneData,
    target: $tips
});

sample({
    clock: getFeaturedFx.doneData,
    target: $featuredTips
});

const contract = new ModelContract(
    {
        $categories,
        $selectedCategory,
        $tips,
        $featuredTips
    },
    {
        setCategory
    },
    {},
    {}
);

export type ITipsContract = typeof contract;

export class TipsModule extends ModuleDescription<ITipsContract> {
    constructor(public userAuthModule: IUserAuthContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.userAuthModule.events.authenticated,
            target: [getCategoriesFx, getFeaturedFx]
        });

        // Category captions and tip copy are translated by the API, so a language
        // switch has to refetch them — the stores would otherwise keep the previous
        // locale on screen until a full reload.
        sample({
            clock: contentLocaleChanged,
            filter: this.userAuthModule.stores.$student.map((student) => Boolean(student)),
            fn: () => undefined,
            target: [getCategoriesFx, getFeaturedFx]
        });

        // The open category keeps its selection across the switch, so its tips are
        // refetched separately — the auto-select sample no longer fires for it.
        sample({
            clock: contentLocaleChanged,
            source: {
                selected: $selectedCategory,
                student: this.userAuthModule.stores.$student
            },
            filter: ({ selected, student }) => Boolean(student) && selected !== null,
            fn: ({ selected }) => selected as number,
            target: getTipsFx
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$categories, $selectedCategory, $tips, $featuredTips]
        });
    }
}
